import { ApiService } from '@supply-mover-2-0/core';
import { REPORT_OUTPUT_FILE_CLAIM_WINDOW_MS, type ReportOutputFileDto, type ReportOutputRequestDto } from '@supply-mover-2-0/shared';
import { create } from 'zustand';
import {
	apiErrorStatus,
	BACKGROUND_DOWNLOAD_MAX_POLL_MS,
	backgroundPollDelay,
	type BackgroundDownloadModel,
	filesToResume,
	isFollowedDownload,
	isPastFollowWindow,
	needsDelivery,
	startBrowserDownload,
} from './backgroundDownloads';

interface EnvelopeModel<T> {
	success: boolean;
	data: T;
}

interface BackgroundDownloadsState {
	items: Array<BackgroundDownloadModel>;
	open: boolean;
	collapsed: boolean;
	setCollapsed: (collapsed: boolean) => void;
	/** Closes the panel and forgets the finished files; files still building keep going. */
	close: () => void;
}

/**
 * PD-17460 — report files built by the background worker, for the Downloads
 * panel. Mounted once in the app shell, so a download keeps going while the
 * person moves between pages.
 */
export const useBackgroundDownloadsStore = create<BackgroundDownloadsState>((set, get) => ({
	items: [],
	open: false,
	collapsed: false,
	setCollapsed: (collapsed) => set({ collapsed }),
	close: () => {
		const active = get().items.filter(isFollowedDownload);
		set({ items: active, open: active.length > 0, collapsed: active.length > 0 });
	},
}));

let pollTimer: ReturnType<typeof setTimeout> | null = null;
/** Checks since the last new request, for the back-off. */
let pollChecks = 0;
let visibilityWatched = false;
/** When the panel last heard about each file, by this browser's clock (PD-17520). */
const lastChecked = new Map<number, number>();

function upsert(file: ReportOutputFileDto, request?: ReportOutputRequestDto | null): void {
	useBackgroundDownloadsStore.setState((state) => {
		const existing = state.items.find((item) => item.file.id === file.id);
		const items =
			existing == null
				? [{ file, request: request ?? null, delivered: file.downloaded, deliveryFailed: false }, ...state.items]
				: state.items.map((item) => (item.file.id === file.id ? { ...item, file } : item));
		return { items, open: true };
	});
}

function markDelivery(id: number, delivered: boolean): void {
	useBackgroundDownloadsStore.setState((state) => ({
		items: state.items.map((item) => (item.file.id === id ? { ...item, delivered, deliveryFailed: !delivered } : item)),
	}));
}

function markLeftToBell(id: number): void {
	useBackgroundDownloadsStore.setState((state) => ({
		items: state.items.map((item) => (item.file.id === id ? { ...item, leftToBell: true } : item)),
	}));
}

function markNotFinished(id: number): void {
	useBackgroundDownloadsStore.setState((state) => ({
		items: state.items.map((item) => (item.file.id === id ? { ...item, notFinished: true } : item)),
	}));
}

/** Drops a file the server no longer has (deleted, or never this person's). */
function remove(id: number): void {
	useBackgroundDownloadsStore.setState((state) => {
		const items = state.items.filter((item) => item.file.id !== id);
		return { items, open: state.open && items.length > 0 };
	});
}

/**
 * Fetches a ready file's link and hands it to the browser. `automatic` is the
 * panel acting on its own: the server gives no link when another tab already
 * downloaded the file, so it is not saved twice. Returns whether a download
 * was started.
 */
export async function deliverBackgroundDownload(id: number, automatic = false): Promise<boolean> {
	const response = await ApiService.apiGet<EnvelopeModel<{ url: string | null; fileName: string }>>(
		`/reporting/files/${id}/download`,
		automatic ? { automatic: true } : undefined,
	);
	markDelivery(id, true);
	if (response.data.url == null) {
		return false;
	}
	startBrowserDownload(response.data.url);
	return true;
}

/**
 * Why a Download from the panel failed, after asking the server about the
 * file again: it expired (the row now says so), it no longer exists (the row
 * is dropped), or the hand-off itself failed.
 */
export async function backgroundDownloadFailure(id: number, error: unknown): Promise<'expired' | 'gone' | 'failed'> {
	if (apiErrorStatus(error) === 404) {
		remove(id);
		return 'gone';
	}
	try {
		const response = await ApiService.apiGet<EnvelopeModel<ReportOutputFileDto>>(`/reporting/files/${id}`);
		upsert(response.data);
		return response.data.status === 'expired' ? 'expired' : 'failed';
	} catch (statusError) {
		if (apiErrorStatus(statusError) === 404) {
			remove(id);
			return 'gone';
		}
		return 'failed';
	}
}

async function pollOnce(): Promise<void> {
	const now = Date.now();
	const followed = useBackgroundDownloadsStore.getState().items.filter(isFollowedDownload);
	await Promise.all(
		followed.map(async (item) => {
			// Past the window the build has stopped or is left to the bell.
			if (isPastFollowWindow(item.file, now)) {
				markNotFinished(item.file.id);
				return;
			}
			try {
				const previous = lastChecked.get(item.file.id) ?? now;
				const response = await ApiService.apiGet<EnvelopeModel<ReportOutputFileDto>>(`/reporting/files/${item.file.id}`);
				lastChecked.set(item.file.id, now);
				upsert(response.data);
				// PD-17520 — it was still building at the last check; if that was longer
				// ago than the bell waits, the bell may already have rung for it. Both
				// times are this browser's, so a server clock that differs cannot matter.
				if (response.data.status === 'ready' && now - previous > REPORT_OUTPUT_FILE_CLAIM_WINDOW_MS) {
					markLeftToBell(item.file.id);
				}
			} catch (error) {
				if (apiErrorStatus(error) === 404) {
					remove(item.file.id);
					return;
				}
				// A missed check is retried on the next tick.
				console.debug('Background download status check failed', error);
			}
		}),
	);
	await deliverReady();
}

/** Ready while the panel was watching (or while the page reloaded): download it now. */
async function deliverReady(): Promise<void> {
	const ready = useBackgroundDownloadsStore.getState().items.filter(needsDelivery);
	for (const item of ready) {
		try {
			await deliverBackgroundDownload(item.file.id, true);
		} catch (error) {
			// The row keeps its Download button for the person to try again.
			console.debug('Background download hand-off failed', error);
			markDelivery(item.file.id, false);
		}
	}
}

const anyFollowed = (): boolean => useBackgroundDownloadsStore.getState().items.some(isFollowedDownload);

/**
 * The next check, backing off from 2 to 30 seconds. A hidden tab keeps
 * checking every 30 seconds (PD-17520): stopping let the bell ring two
 * minutes after the file was ready AND the file download when the tab was
 * shown again. It checks again promptly when the tab is shown.
 */
function schedulePoll(): void {
	watchVisibility();
	if (pollTimer != null) {
		return;
	}
	pollTimer = setTimeout(
		() => {
			pollTimer = null;
			pollChecks++;
			void pollOnce().finally(() => {
				if (anyFollowed()) {
					schedulePoll();
				}
			});
		},
		document.hidden ? BACKGROUND_DOWNLOAD_MAX_POLL_MS : backgroundPollDelay(pollChecks),
	);
}

/** Checks again from the shortest wait: a new request, or the tab shown again. */
function restartPolling(): void {
	if (pollTimer != null) {
		clearTimeout(pollTimer);
		pollTimer = null;
	}
	pollChecks = 0;
	schedulePoll();
}

function watchVisibility(): void {
	if (visibilityWatched) {
		return;
	}
	visibilityWatched = true;
	document.addEventListener('visibilitychange', () => {
		if (!document.hidden && anyFollowed()) {
			restartPolling();
		}
	});
}

/** Queues a report file and follows it in the Downloads panel. */
export async function requestBackgroundDownload(request: ReportOutputRequestDto): Promise<ReportOutputFileDto> {
	const response = await ApiService.apiPost<EnvelopeModel<ReportOutputFileDto>>('/reporting/files', request);
	lastChecked.set(response.data.id, Date.now());
	upsert(response.data, request);
	useBackgroundDownloadsStore.setState({ collapsed: false });
	restartPolling();
	return response.data;
}

/**
 * Asks for a failed (or unfinished) file again, as a new file. The old row
 * stays until the new request is accepted, so a failed request leaves the
 * row and its Try again in place; the error is thrown for the panel to show.
 */
export async function retryBackgroundDownload(id: number): Promise<void> {
	const item = useBackgroundDownloadsStore.getState().items.find((row) => row.file.id === id);
	if (item?.request == null) {
		return;
	}
	await requestBackgroundDownload(item.request);
	useBackgroundDownloadsStore.setState((state) => ({ items: state.items.filter((row) => row.file.id !== id) }));
}

/** After a reload: picks up files still being built, and downloads any that finished meanwhile. */
export async function resumeBackgroundDownloads(): Promise<void> {
	try {
		const response = await ApiService.apiGet<EnvelopeModel<Array<ReportOutputFileDto>>>('/reporting/files');
		// The bell's download page fetches its own file; picking it up here too would save it twice.
		const opened = Number(/^\/reporting\/downloads\/(\d+)/.exec(window.location.pathname)?.[1]);
		const files = filesToResume(response.data ?? [], Date.now()).filter((file) => file.id !== opened);
		for (const file of files) {
			upsert(file);
		}
		if (anyFollowed()) {
			schedulePoll();
		}
		await deliverReady();
	} catch (error) {
		// Without the list the bell still brings any file that finishes.
		console.debug('Could not load recent downloads', error);
	}
}
